import { fitTitle, fitDescription } from "@/lib/seo-text";
import { Link, createFileRoute } from "@tanstack/react-router";

import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FIRM, faqSchema } from "@/lib/firm";

const FEES = {
  grant: "$3,300",
  admin: "$8,800",
} as const;

const FAQS = [
  {
    q: "When is probate required in Queensland?",
    a: "Usually when the deceased owned real estate in their sole name, or held bank accounts, shares or investments above the institution's limit for releasing funds without a grant. Property owned as joint tenants passes to the survivor, and superannuation and life insurance often sit outside the estate, so not every estate needs probate.",
  },
  {
    q: "What is a probate notice?",
    a: "Before applying, the executor must publish a notice of intention to apply for a grant and serve a copy on the Public Trustee, then wait at least 14 days. We publish and serve the notice as part of our fixed fee.",
  },
  {
    q: "Can probate be done entirely online in Queensland?",
    a: "Almost all of it. The application, the executor's affidavit and the notices are prepared and exchanged electronically, and identity can be verified by video. The one physical step is the original signed will, which the Supreme Court requires — it is posted or handed to us, and we hold it in safe custody until the grant issues.",
  },
  {
    q: "How much does probate cost in Queensland?",
    a: `Our fixed professional fee for a straightforward grant on its own is ${FEES.grant} including GST, plus the Supreme Court filing fee and the cost of the published notice, which are set by others and charged at cost. Full estate administration — including obtaining the grant — is one all-inclusive fixed fee of ${FEES.admin} including GST, covering up to 3 bank accounts, the retirement village exit or one property transfer, and distribution to up to 6 beneficiaries — more complex estates quoted.`,
  },
  {
    q: "How long does probate take?",
    a: "The notice of intention to apply must be published and served on the Public Trustee, and the application cannot be filed for at least 14 days after that. Where the paperwork is clean, a grant commonly issues six to ten weeks from the day we start.",
  },
  {
    q: "Do I need a lawyer near me to do probate?",
    a: "No. Probate is a Supreme Court of Queensland matter, not a local one, so where the executor lives makes no difference. We act for executors from Cairns to Coolangatta and everything is handled by phone, email and video.",
  },
  {
    q: "What if the original will is held by another law firm?",
    a: "We write to the firm, bank or the Public Trustee holding it in safe custody and request its release to us as the executor's solicitors. That is included in the fixed fee — you do not have to negotiate with them yourself.",
  },
  {
    q: "What if there is no will?",
    a: "The application becomes one for letters of administration, and who may apply is set by the intestacy rules. The process, the notices and our fixed fee are the same; there is simply more work evidencing the entitled next of kin.",
  },
  {
    q: "Do you charge a percentage of the estate?",
    a: "No. A larger estate does not make the application harder, so it should not make the fee bigger. Trustee companies and some firms charge a percentage; we do not.",
  },
];

const STEPS = [
  {
    t: "1. Executor intake, online",
    d: "You complete the intake form with the will, the assets and the date of death. We come back the same or next business day with whether a grant is actually needed and the fee in writing.",
  },
  {
    t: "2. Identity and the original will",
    d: "Identity is verified by video. The original signed will comes to us — from you, or released from another firm's safe custody after we write for it.",
  },
  {
    t: "3. Notice published, Public Trustee served",
    d: "We publish the notice of intention to apply and serve the Public Trustee of Queensland. The application cannot be filed until at least 14 days later.",
  },
  {
    t: "4. Application filed electronically",
    d: "The application, affidavit and supporting material are filed in the Supreme Court of Queensland. We deal with any registry requisitions ourselves.",
  },
  {
    t: "5. Grant issues, estate administered",
    d: "Typically six to ten weeks in. From there we can also collect the assets, transfer or sell property, deal with super, insurers and the ATO, prepare estate accounts and distribute — scoped separately.",
  },
];

export const Route = createFileRoute("/probate-queensland")({
  head: () => ({
    meta: [
      { title: fitTitle("Probate & Deceased Estate Administration QLD | Coastside Law") },
      {
        name: "description",
        content:
          fitDescription("Queensland probate and deceased estate administration. Solicitor-led help with grants, estate assets and distributions. Grant fee $3,300 incl GST plus costs."),
      },
      { property: "og:title", content: fitTitle(`Probate & deceased estate administration QLD | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Help for Queensland executors: probate, letters of administration and the work of collecting assets, paying debts and distributing an estate."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/probate-queensland" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/probate-queensland" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: ProbateQueenslandPage,
});

function ProbateQueenslandPage() {
  return (
    <>
      <PageHero
        personSlug="michael-klein"
        eyebrow="Probate Queensland — online, state-wide"
        title="Probate in Queensland: what it is, when you need it, and a fixed fee to handle it"
        lead="Probate is the Supreme Court of Queensland confirming that a will is valid and the executor can deal with the estate. Executors anywhere in Queensland can start an estate with us online. Fixed professional fee, solicitor-led, everything handled by email and video — the only thing that has to travel is the original will."
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/executor-intake"
            className="inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Start the executor intake
          </Link>
          <a
            href={FIRM.phoneHref}
            className="inline-flex items-center justify-center rounded-sm border border-border px-6 py-3 text-sm font-semibold text-foreground"
          >
            Call {FIRM.phone}
          </a>
        </div>
      </PageHero>

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { t: FEES.grant, d: "Fixed fee for a grant of probate or letters of administration, incl GST" },
            { t: "6–10 weeks", d: "Typical time from starting to the grant issuing, where papers are clean" },
            { t: "0 office visits", d: "Video ID, electronic filing, the original will by post" },
          ].map((c) => (
            <div key={c.t} className="rounded-sm border border-border bg-card p-6">
              <p className="font-serif text-2xl text-primary">{c.t}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Probate and deceased estate administration: what is the difference?</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Probate is a court grant recognising the will and the executor's authority. Deceased
          estate administration is the broader work of identifying and protecting assets, dealing
          with liabilities and distributing the estate. A grant may be one step in that process,
          but receiving it does not mean the estate is ready to distribute.
        </p>
        <h3 className="mt-8 font-serif text-2xl text-foreground">Does every estate need probate?</h3>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          No. The answer depends on the assets, how they are owned and the requirements of the
          organisations holding them. Check with the relevant bank or institution before assuming
          a grant is necessary. If there is no will, or the named executor cannot act, a different
          type of grant may be appropriate.
        </p>
        <h3 className="mt-8 font-serif text-2xl text-foreground">What happens after probate is granted in Queensland?</h3>
        <ul className="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-muted-foreground">
          <li>Confirm what each asset holder needs to release or transfer estate assets.</li>
          <li>Collect assets and arrange any property sale or transfer required for the estate.</li>
          <li>Address debts, tax obligations and any unresolved claims before distributing.</li>
          <li>Keep estate accounts and distribute according to the will or applicable intestacy rules.</li>
        </ul>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Tell us whether you need help obtaining the grant, finishing an estate after a grant,
          or managing the whole administration. Read our{" "}
          <Link to="/guides/$slug" params={{ slug: "executor-duties-in-queensland" }} className="font-semibold text-primary underline">executor duties guide</Link>{" "}
          or <Link to="/probate-enquiry" className="font-semibold text-primary underline">request an estate administration quote</Link>.
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          Sources: <a href="https://www.courts.qld.gov.au/types-of-cases/about-grants-of-administration" className="text-primary underline">Queensland Courts guidance on grants</a> and{" "}
          <a href="https://www.pt.qld.gov.au/wills-powers-of-attorney-estates/deceased-estates/about-deceased-estates" className="text-primary underline">Queensland Public Trustee guidance on administering an estate</a>.
          General information only; advice depends on the estate's circumstances.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">What it costs</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          A grant of probate confirms the executor's authority; administering the estate is the
          later work of collecting assets, paying debts and distributing to beneficiaries.
          The grant-only fee is not a quote for that whole process. Read our{" "}
          <Link to="/guides/$slug" params={{ slug: "how-much-does-probate-cost-in-queensland" }} className="font-semibold text-primary underline">probate cost guide</Link>{" "}
          or <Link to="/probate-enquiry" className="font-semibold text-primary underline">tell us about the estate for a scoped quote</Link>.
        </p>
        <div className="mt-6 rounded-sm border border-border bg-card p-7">
          <dl className="space-y-4 text-sm leading-relaxed">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/60 pb-4">
              <dt className="text-foreground">
                Grant of probate, or letters of administration
                <span className="block text-muted-foreground">Fixed professional fee, including GST</span>
              </dt>
              <dd className="font-serif text-2xl text-primary">{FEES.grant}</dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/60 pb-4">
              <dt className="text-foreground">
                Full estate administration, including the grant of probate
                <span className="block text-muted-foreground">
                  All-inclusive fixed fee including GST — the grant itself, plus up to 3 bank
                  accounts, the retirement village exit or one property transfer, and distribution
                  to up to 6 beneficiaries. More complex estates quoted.
                </span>
              </dt>
              <dd className="font-serif text-2xl text-primary">{FEES.admin}</dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <dt className="text-foreground">
                Disbursements
                <span className="block text-muted-foreground">
                  Supreme Court filing fee and the published notice — set by others, charged at cost
                </span>
              </dt>
              <dd className="text-sm text-muted-foreground">At cost</dd>
            </div>
          </dl>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            No percentage of the estate, no hourly billing on the grant, and the estate normally pays.
          </p>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">How the online process runs</h2>
        <div className="mt-8 space-y-5">
          {STEPS.map((s) => (
            <div key={s.t} className="rounded-sm border-l-2 border-accent bg-secondary/40 p-6">
              <h3 className="font-serif text-xl text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Can't find the original will?</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Most originals sit in a law firm's safe custody, a bank, or with the Public Trustee —
            often a firm that has since merged or closed. Tell us the name and we will chase it.
          </p>
          <Link
            to="/will-safe-custody"
            className="mt-4 inline-block text-sm font-semibold text-primary underline"
          >
            How to find a will held in safe custody in Queensland
          </Link>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who will handle it" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <p className="mt-10 text-sm text-muted-foreground">
          Read our guides on{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "how-long-does-probate-take-in-queensland" }}
            className="font-semibold text-primary underline"
          >
            how long probate takes
          </Link>
          ,{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "how-to-apply-for-probate-in-queensland" }}
            className="font-semibold text-primary underline"
          >
            how to apply for probate
          </Link>
          ,{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "executor-duties-in-queensland" }}
            className="font-semibold text-primary underline"
          >
            what an executor actually has to do
          </Link>{" "}
          and{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "do-i-need-probate-in-queensland" }}
            className="font-semibold text-primary underline"
          >
            whether you need a grant at all
          </Link>
          . Prefer a local page? See{" "}
          <Link to="/probate" className="font-semibold text-primary underline">
            probate on the Sunshine Coast
          </Link>
          . Outside the Sunshine Coast? We act for executors state-wide —{" "}
          <Link to="/remote-probate" className="font-semibold text-primary underline">
            remote probate anywhere in Queensland
          </Link>
          . If another firm holds the original will, we can{" "}
          <Link
            to="/transfer-my-will-from-another-firm"
            className="font-semibold text-primary underline"
          >
            write for its release
          </Link>
          .
        </p>
      </section>

        <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
          Executor in Brisbane or elsewhere in Queensland?{" "}
          <Link to="/probate-solicitor-brisbane" className="font-semibold text-primary underline">
            See our probate solicitor page for Brisbane and QLD executors
          </Link>
          .
        </p>

      <section className="mx-auto max-w-3xl px-5 pb-16">
        <ExecutorIntakeForm heading="Start a probate enquiry" />
      </section>

      <CtaBand
        title="Start the estate online today"
        body={`Complete the executor intake and we will tell you whether a grant is needed and what it costs, in writing. Or call ${FIRM.phone}.`}
      />
      <div className="h-20" />
    </>
  );
}
